import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
  type Uuid,
} from '@shared/types/api';
import {
  useActivateService,
  useAdminServicesList,
  useDeactivateService,
  useReorderServices,
} from '@/hooks/use-admin';
import { CategoryChip } from './services/category-chip';
import { ServiceEditorDialog } from './services/service-editor-dialog';
import { ServicesTable } from './services/services-table';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

export function AdminServicesPage() {
  const { canCreate, canEdit } = useSectionPermission('SERVICES');
  const [editing, setEditing] = useState<ServiceSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [filter, setFilter] = useState<ServiceCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const activate = useActivateService();
  const deactivate = useDeactivateService();
  const reorder = useReorderServices();
  const queryClient = useQueryClient();

  const queryParams = {
    page,
    pageSize,
    status: statusFilter,
    category: filter === 'ALL' ? undefined : filter,
    q: search.trim() || undefined,
    includeInactive: true,
  };

  const { data: pageData, isLoading } = useAdminServicesList(queryParams);

  const services = pageData?.items ?? [];
  const total = pageData?.total ?? 0;
  const totalPages = pageData?.totalPages ?? 1;
  const activeCount = pageData?.activeCount ?? 0;
  const inactiveCount = pageData?.inactiveCount ?? 0;

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const items = [...services];
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(index, 0, draggedItem);

    queryClient.setQueryData(
      ['admin', 'services', 'list', queryParams],
      (old: any) => (old ? { ...old, items } : old),
    );
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    const ids = services.map((s) => s.id as Uuid);
    const startIndex = (page - 1) * pageSize;
    try {
      await reorder.mutateAsync({ ids, startIndex });
    } catch (err) {
      console.error('Failed to save service order', err);
    }
  };

  const handleStatusFilterChange = (status: 'ACTIVE' | 'INACTIVE') => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleCategoryChange = (cat: ServiceCategory | 'ALL') => {
    setFilter(cat);
    setPage(1);
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Services</h1>
          <p className="text-sm text-gray-600">Catalog of services clients can order.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreating(true)}>New service</Button>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="mt-4 flex items-center overflow-x-auto no-scrollbar scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200 gap-2">
        <button
          type="button"
          onClick={() => handleStatusFilterChange('ACTIVE')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            statusFilter === 'ACTIVE'
              ? 'border-brand-600 text-brand-800'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Active Services</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              statusFilter === 'ACTIVE'
                ? 'bg-brand-100 text-brand-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {activeCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => handleStatusFilterChange('INACTIVE')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            statusFilter === 'INACTIVE'
              ? 'border-red-600 text-red-700'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Deactivated Services</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              statusFilter === 'INACTIVE'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {inactiveCount}
          </span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mt-4">
        <div className="relative max-w-md">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search service name, category, description, HSN..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          <CategoryChip label="All" active={filter === 'ALL'} onClick={() => handleCategoryChange('ALL')} />
          {SERVICE_CATEGORIES.map((cat) => (
            <CategoryChip
              key={cat}
              label={SERVICE_CATEGORY_LABELS[cat]}
              active={filter === cat}
              onClick={() => handleCategoryChange(cat)}
            />
          ))}
        </div>

        <div className="hidden sm:block shrink-0">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isLoading}
            showBorder={false}
            showRange={false}
            showPageSize={false}
            compact
          />
        </div>
      </div>

      <ServicesTable
        services={services}
        isLoading={isLoading}
        statusFilter={statusFilter}
        draggedIndex={draggedIndex}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onEdit={setEditing}
        onActivate={(id) => activate.mutate(id)}
        isActivating={activate.isPending}
        onDeactivate={(id) => deactivate.mutate(id)}
        isDeactivating={deactivate.isPending}
      />

      <div className="mt-4">
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          isLoading={isLoading}
          className="rounded-lg border border-gray-200"
        />
      </div>

      {creating && canCreate ? (
        <ServiceEditorDialog existingServices={services} onClose={() => setCreating(false)} />
      ) : null}
      {editing ? (
        <ServiceEditorDialog
          existing={editing}
          existingServices={services}
          readOnly={!canEdit}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
